import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AlertService } from 'src/app/shared/services/alert.service';
import { PasswordService } from 'src/app/shared/services/password/password.service';
import { UsersService } from 'src/app/shared/services/users/users.service';
import {
  asyncPasswordValidator,
  passwordConfirmationValidator,
} from 'src/app/shared/validators/validators';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styles: [':host{width: 100%; max-width: 334px;}'],
})
export class PasswordResetComponent implements OnInit, OnDestroy {
  resetPasswordForm: UntypedFormGroup;
  hostURL: string = location.href;

  isPasswordReset = false;
  isUrlLinkValid: boolean;
  typeOfLink: string;

  isLoading = false;
  subscriptions: Subscription = new Subscription();

  constructor(
    private router: Router,
    private usersService: UsersService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private passwordService: PasswordService,
  ) {}

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.typeOfLink = this.route.snapshot.queryParams.type;

    this.isPasswordReset = this.typeOfLink == 'P' ? true : false;

    this.validateUrl(this.hostURL)
      .then(() => {
        this.resetPasswordForm = this.fb.group(
          {
            password: [
              undefined,
              Validators.required,
              asyncPasswordValidator(this.passwordService),
            ],
            confirmPassword: [
              undefined,
              Validators.required,
              asyncPasswordValidator(this.passwordService),
            ],
          },
          {
            validators: passwordConfirmationValidator(
              'password',
              'confirmPassword',
            ),
          },
        );

        if (!this.isPasswordReset) {
          this.resetPasswordForm.addControl(
            'pin',
            new UntypedFormControl(undefined, [
              Validators.required,
              Validators.minLength(4),
              Validators.maxLength(4),
            ]),
          );
        }
      })
      .catch(() => {
        this.alertService.error('An unexpected error occurred');
      });
  }

  onSubmit() {
    this.isLoading = true;

    const formValue = this.resetPasswordForm.value;
    const { pin } = formValue;
    const linkID = this.route.snapshot.queryParams.linkid;
    const params = {
      userLinkId: linkID,
      password: formValue.password,
      confirmPassword: formValue.confirmPassword,
    };

    const request$ = this.isPasswordReset
      ? this.usersService.updatePasswordAfterReset(params)
      : this.usersService.createPassword(params);

    if (this.isPasswordReset) {
      this.subscriptions.add(
        request$.subscribe({
          next: () => {
            this.creationSuccess().catch(() => {
              this.alertService.error('An unexpected error occurred');
            });
          },
          error: (error: unknown) => {
            this.isLoading = false;
            this.alertService.error(error as HttpErrorResponse);
          },
        }),
      );
    } else {
      this.subscriptions.add(
        this.usersService
          .validateUrlAndPin({
            userLinkUrl: this.hostURL,
            pin,
          })
          .pipe(
            mergeMap((params) => {
              return params.isValid
                ? request$
                : throwError(() => {
                    return params.validationInfo;
                  });
            }),
          )
          .subscribe({
            next: () => {
              this.creationSuccess().catch(() => {
                this.alertService.error('An unexpected error occurred');
              });
            },
            error: (error: unknown) => {
              this.isLoading = false;
              if (
                error instanceof HttpErrorResponse ||
                error instanceof TypeError
              ) {
                this.alertService.error(error);
              } else {
                this.alertService.error('An unexpected error occurred');
              }
            },
          }),
      );
    }
  }

  private async creationSuccess() {
    const title = this.isPasswordReset
      ? 'Password has been changed successfully'
      : 'Password has been created successfully';
    const text = 'Redirecting to login page...';

    this.alertService.success(title, text);

    await new Promise((resolve) => {
      return setTimeout(resolve, 1000);
    });

    await this.router.navigate(['/login']);
  }

  private async validateUrl(url: string) {
    try {
      const response = await firstValueFrom(
        this.usersService.validateUrl({ userLinkUrl: url }),
      );

      if (response.isValid) {
        this.isUrlLinkValid = true;
      } else {
        await this.router.navigate(['/login']);
      }
    } catch (_error: unknown) {
      await this.router.navigate(['/login']);
    }
  }
}
